import { Fragment, useState, useEffect } from "react";
import {
  Modal,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Icon,
  Text,
  Flex,
  FieldTextGroup
} from "@theorgchartdev/storybook";
import { GetWSMgr, GetDoc } from "@/Utils/StateLib";
import { GetUserList } from "@/Utils/Promises";
import { LinkUserSection } from "./LinkUserSection/LinkUserSection";
import { LaunchConfigSection } from "./LaunchConfigSection/LaunchConfigSection";
import { ParametersSection } from "./ParametersSection/ParametersSection";
import { StartPointSection } from "./StartPointSection/StartPointSection";
import { LinkSection } from "./LinkSection/LinkSection";
import {
  GenerateLink,
  TestLink,
  linkClass
} from "../../../Navigation/Header/WebLinkMenu/WebLinkUtils";
import { OCLink } from "@/appComponents/Blocks/Utils/OCLink";
import { appstr } from "@coreapp/utils/appstr";
import { enumChartTemplateSet } from "@coreapp/constants";

const LinkRecipientSection = ({ role, group, profile }) => {
  const uiProfileLink = (
    <Flex padding={"14px 0px 0px"}>
      <OCLink
        to={`/settings/account/ui-profiles/${profile}`}
        className={linkClass}
        data-testid={"weblink-modify-ui-profile-link"}
      >
        {appstr("Modify UI Profile")}
      </OCLink>
    </Flex>
  );

  const defaultPlaceholder = "<Not Set>";

  const groupdata = [
    { field: "User Role", value: role, placeHolder: defaultPlaceholder },
    { field: "Access Group", value: group, placeHolder: defaultPlaceholder },
    { field: "UI Profile", value: profile, placeHolder: defaultPlaceholder },
    { field: "", value: uiProfileLink, placeHolder: "" }
  ];
  return (
    <Fragment>
      <FieldTextGroup data={groupdata} columns={2} columnGap={"262"} size={"large"} />
    </Fragment>
  );
};

const WebLinkModal = ({ show, onClose, linkType, titleProps }) => {
  const wsmgr = GetWSMgr();
  const currentChartID = GetDoc()?.GetChartID();
  const orgInfo = wsmgr.GetOrgInfo();
  const orgName = wsmgr.GetAccountName();
  const baseCharts = orgInfo.GetMasterChartList();
  const ctm = wsmgr.GetChartTemplatesMgr();
  const sharedViews = ctm.GetTemplateList(enumChartTemplateSet.sharedtemplates);
  const sysViews = ctm.GetTemplateList(enumChartTemplateSet.systemtemplates);
  const views = sysViews.concat(sharedViews);

  const [urlParamsMgr, setUrlParamsMgr] = useState({
    linkUserConfig: { linkUser: null, saml: false, entityID: "" },
    launchConfig: {
      launchMode: "Chart",
      chartType: 0,
      chartID: currentChartID != null ? currentChartID : null,
      plan: null,
      directory: null,
      fileName: null
    },
    startPoint: { topBoxID: null, levels: null, view: null },
    uiProfile: "default",
    parameters: null,
    iframe: false,
    link: "",
    error: null
  });

  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState({});
  const currentUserInfo = users.find(x => x["username"] === urlParamsMgr.linkUserConfig.linkUser);
  let currentGroupName = "Full Access";

  const LoadUserList = async () => {
    const { userList, groupInfo } = await GetUserList();
    setUsers(userList);
    setGroups(groupInfo.groups);
  };

  useEffect(() => {
    LoadUserList();
  }, []);

  const GetUIProfile = () => {
    const currentGroup = groups[currentUserInfo.group];
    if (currentGroup?.uiprofile) return currentGroup.uiprofile;

    return "default";
  };

  useEffect(() => {
    if (currentUserInfo)
      setUrlParamsMgr(prevState => ({ ...prevState, uiProfile: GetUIProfile() }));
  }, [urlParamsMgr.linkUserConfig]);

  if (currentUserInfo && currentUserInfo.group !== "0") {
    currentGroupName = groups[currentUserInfo.group].name;
  }

  const setError = errorText => {
    setUrlParamsMgr(prevState => ({ ...prevState, error: errorText }));
  };

  return (
    <Modal
      id="oc-weblink-modal"
      show={show}
      onClose={onClose}
      centerScreen={true}
      fullwidth={false}
      customStyle={{ padding: 0, width: "640px" }}
    >
      <DialogTitle onClose={onClose} divider={true}>
        <span className={"ocn-explorer-icon-label"}>
          <Icon size={"medium"} name={titleProps.icon} />
          {appstr(titleProps.label)}
        </span>
      </DialogTitle>

      <DialogContent dividers={true}>
        <Flex padding={"16px 0px 0px 0px"}>
          <Text size={"small"}>{appstr(titleProps.description)}</Text>
        </Flex>
        <LinkUserSection
          users={users}
          linkType={linkType}
          linkUserConfig={urlParamsMgr.linkUserConfig}
          setUrlParamsMgr={setUrlParamsMgr}
          setError={setError}
        />
        {urlParamsMgr?.error && (
          <Text size={"xsmall"} colorModifier={"error"}>
            {urlParamsMgr.error}
          </Text>
        )}
      </DialogContent>

      {currentUserInfo ? (
        <DialogContent dividers={true}>
          <LinkRecipientSection
            role={currentUserInfo.role}
            group={currentGroupName}
            profile={urlParamsMgr.uiProfile}
          />
        </DialogContent>
      ) : null}

      <DialogContent dividers={true}>
        <LaunchConfigSection
          launchConfig={urlParamsMgr.launchConfig}
          baseCharts={baseCharts}
          setUrlParamsMgr={setUrlParamsMgr}
        />
      </DialogContent>

      <DialogContent dividers={true}>
        <StartPointSection
          views={views}
          startPointConfig={urlParamsMgr.startPoint}
          setUrlParamsMgr={setUrlParamsMgr}
        />
      </DialogContent>

      <DialogContent dividers={true}>
        <ParametersSection
          currentParams={urlParamsMgr.parameters}
          setUrlParamsMgr={setUrlParamsMgr}
        />
      </DialogContent>

      <DialogContent dividers={true}>
        <LinkSection linkConfig={urlParamsMgr.link} setUrlParamsMgr={setUrlParamsMgr} />
      </DialogContent>

      <DialogActions>
        <DialogActions.Tertiary onClick={onClose} dataTestId={"cancel-weblink-btn"}>
          {appstr("Cancel")}
        </DialogActions.Tertiary>
        <DialogActions.Primary
          onClick={() => GenerateLink(urlParamsMgr, setUrlParamsMgr, linkType, orgName, setError)}
          dataTestId={"generate-link-btn"}
        >
          {appstr("genlink")}
        </DialogActions.Primary>
        <DialogActions.Secondary
          disabled={urlParamsMgr.link === ""}
          onClick={() =>
            TestLink(
              urlParamsMgr.link,
              linkType,
              urlParamsMgr.linkUserConfig.linkUser,
              orgName,
              setError
            )
          }
          dataTestId={"test-link-btn"}
        >
          {appstr("testlink")}
        </DialogActions.Secondary>
      </DialogActions>
    </Modal>
  );
};

export default WebLinkModal;
